/*
  ClientDetail — per-client hub. Route: /clients/:emailEncoded

  Five tabs:
    Overview     — contact info, PDQ / ONERO trends, activity counts, actions
    Notes        — SOAP notes list + Record button
    Forms        — intake submissions timeline
    Messages     — SMS conversation (via /sms/conversation/{phone})
    Appointments — Acuity bookings for this email
*/
import { isoDate as melDate } from "@/lib/intl";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { downloadCompactOnboardingPdf } from "@/lib/compactPdf";
import { useAuth } from "@/lib/auth";
import { CanonicalClientContext } from "@/lib/canonicalClient";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClientCheckinsTab } from "@/components/checkins/client/ClientCheckinsTab";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Mail, Phone, Mic, MessageSquare, Calendar, FileText,
  StickyNote, Send, Loader2, User, ExternalLink, Plus, Copy, ArrowRight,
  Star, AlertTriangle, ClipboardList, Wand2, Split, Users, Pencil,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import SoapSessionDialog, { SoapNotesList } from "@/components/SoapSessionDialog";
import QuickSmsDialog from "@/components/QuickSmsDialog";
import NominateForReviewDialog from "@/components/NominateForReviewDialog";
import HealthRecordTab from "@/pages/HealthRecordTab";
import ClientDetailsTab from "@/pages/ClientDetailsTab";
import ClinicalSummaryCard from "@/components/ClinicalSummaryCard";
import { ClientFilesSection } from "@/components/files/ClientFilesSection";
import CriticalAlertsStrip from "@/components/CriticalAlertsStrip";
import ExerciseProgramsPanel from "@/components/ExerciseProgramsPanel";
import ClientPainTrendCard from "@/components/ClientPainTrendCard";
import OnboardingReviewTab from "@/pages/OnboardingReviewTab";
import { useActiveClient } from "@/lib/activeClient";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Activity, Link2, Link2Off, Search, GitCompare } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { IntakeFormPicker } from "@/components/assessment/IntakeFormPicker";
import { PrintFormButton } from "@/components/assessment/print/PrintFormDialog";
import { MeasurementTrend, useMeasurementPoints } from "@/components/assessment/MeasurementTrend";
import { MeasurementProgressCard } from "@/components/assessment/MeasurementProgressCard";

function fmtDT(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}
function fmtD(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(); } catch { return String(v); }
}

export default function ClientDetail() {
  const { emailEncoded } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { can } = useAuth();
  // Phase-2 scoped view — when a user opens `/clients/<email>?as=<cid>`
  // we filter every clinical tab down to that canonical client's
  // records + stamp new writes with the canonical id. Empty when
  // the caller is on the legacy shared-email URL, which keeps
  // behaviour identical to Phase 1.
  const canonicalClientId = searchParams.get("as") || null;
  const [canonicalIdentity, setCanonicalIdentity] = useState(null);
  // All active staff can record assessments + send SMS.
  const editable = can("owner", "manager", "staff");
  const email = decodeURIComponent(emailEncoded || "");
  const { setClient: setActiveClient } = useActiveClient();
  const [profile, setProfile] = useState(null);   // aggregate row from /intake/clients
  const [loading, setLoading] = useState(true);
  const [soapOpen, setSoapOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [nominateOpen, setNominateOpen] = useState(false);
  const [soapRefresh, setSoapRefresh] = useState(0);
  const [tab, setTab] = useState(searchParams.get("tab") || "record");
  // Increment to signal AssessmentsTab that a caller wants the "New
  // assessment" dialog auto-opened as soon as the tab renders. Used by
  // HealthRecordTab's "New assessment" button so one click switches
  // tabs AND kicks off the dialog with the client details pre-filled.
  const [newAssessmentSignal, setNewAssessmentSignal] = useState(0);
  // Canonical client rows sharing this email (Phase-1 client_id migration).
  // Used to power the "View canonical record" links + the Split button
  // in the header so operators can reach the split UI from the page they
  // already work on.
  const [canonicalRows, setCanonicalRows] = useState([]);
  const [renameOpen, setRenameOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);

  function openNewAssessment() {
    setNewAssessmentSignal((n) => n + 1);
    setTab("assessments");
  }

  useEffect(() => {
    if (!email) return;
    // Remember this client globally so downstream screens (Letters, etc.)
    // can offer a one-tap "back to <client>" link. When scoped via
    // `?as=<cid>`, the canonical effect below owns the name — don't
    // flash an empty name here.
    if (!canonicalClientId) setActiveClient({ email, name: "" });
    setLoading(true);
    // Fetch BOTH the aggregated /intake/clients row (for phone, last
    // activity, submission counts) AND the merged profile (for the
    // authoritative display name). When a parent and child share an
    // email, the intake list's ``client_name`` may reflect whichever
    // form was submitted most recently — which can disagree with the
    // Details tab. Preferring the profile's ``full_name`` keeps the
    // header and the body in lock-step.
    Promise.all([
      api.get("/intake/clients", { params: { q: email } })
        .then((r) => (r.data || []).find(
          (x) => (x.client_email || "").toLowerCase() === email.toLowerCase(),
        ) || null)
        .catch(() => null),
      api.get(`/clients/${encodeURIComponent(email)}/profile`,
        { params: canonicalClientId ? { canonical_client_id: canonicalClientId } : {} })
        .then((r) => r.data || {})
        .catch(() => ({})),
    ])
      .then(([match, prof]) => {
        const derivedName =
          (prof?.profile?.full_name || "").trim() ||
          [prof?.profile?.first_name, prof?.profile?.middle_name, prof?.profile?.last_name]
            .filter(Boolean).join(" ").trim() ||
          (match?.client_name || "");
        const merged = {
          ...(match || { client_email: email }),
          client_name: derivedName,
          // Details-tab mobile (edited > onboarding answer > Acuity) beats the list row's appointment phone.
          client_phone: (prof?.profile?.mobile || "").trim() || match?.client_phone || "",
          // Attach only the aliases that aren't identical to the
          // derived name so a "no-op" alias never renders.
          name_aliases: prof?.name_aliases || [],
        };
        setProfile(merged);
        // Only sync the global activeClient from the shared-email profile
        // when there's no canonical scope in play — otherwise the canonical
        // identity effect owns the name and phone.
        if (derivedName && !canonicalClientId) setActiveClient({ email, name: derivedName });
      })
      .catch((e) => toast.error("Load failed", { description: e?.response?.data?.detail || e.message }))
      .finally(() => setLoading(false));
  }, [email, setActiveClient, canonicalClientId]);

  // Load canonical identity when `?as=<cid>` is present so the
  // header can say "Editing as Judy Van Rooyen" and the context
  // provider carries the display name to child tabs.
  useEffect(() => {
    if (!canonicalClientId) { setCanonicalIdentity(null); return; }
    let cancelled = false;
    api.get(`/clients/id/${encodeURIComponent(canonicalClientId)}`)
      .then((r) => {
        if (cancelled) return;
        const fullName = r.data?.full_name || "";
        const phone = r.data?.phone || r.data?.mobile || "";
        setCanonicalIdentity({
          clientId: canonicalClientId,
          fullName,
          phone,
        });
        // Keep the global "activeClient" (used by Letters, SbN, back-links)
        // in lock-step with the scoped canonical person, not whichever
        // sibling happens to be the primary on the shared email.
        if (fullName && email) setActiveClient({ email, name: fullName, canonical_client_id: canonicalClientId });
      })
      .catch(() => {
        if (!cancelled) setCanonicalIdentity({ clientId: canonicalClientId, fullName: "", phone: "" });
      });
    return () => { cancelled = true; };
  }, [canonicalClientId, email, setActiveClient]);

  // Load every canonical `clients` row that shares this email so the
  // header can surface the "View canonical record" links + Split button.
  // Runs independently from the profile fetch so a Phase-1 backfill lag
  // never blocks the main page render.
  useEffect(() => {
    if (!email) { setCanonicalRows([]); return; }
    let cancelled = false;
    api.get("/clients/by-email", { params: { email } })
      .then((r) => {
        if (cancelled) return;
        setCanonicalRows(Array.isArray(r.data?.clients) ? r.data.clients : []);
      })
      .catch(() => { if (!cancelled) setCanonicalRows([]); });
    return () => { cancelled = true; };
  }, [email]);

  // Auto-scope: when the operator lands on ``/clients/<email>``
  // *without* ``?as=`` and the email resolves to at least one
  // canonical row, redirect so the workspace opens on a specific
  // person's isolated view — never on the ambiguous merged view.
  // We pick the first canonical (earliest ``created_at``) as the
  // default; the sibling switcher in the header handles moving
  // between people. Uses ``replace`` so Back never returns here.
  useEffect(() => {
    if (canonicalClientId) return;              // already scoped
    if (!canonicalRows || canonicalRows.length === 0) return;
    const primary = canonicalRows[0];
    if (!primary?._id) return;
    const next = new URLSearchParams(searchParams);
    next.set("as", primary._id);
    navigate(
      { pathname: `/clients/${encodeURIComponent(email)}`, search: `?${next.toString()}` },
      { replace: true },
    );
  }, [canonicalClientId, canonicalRows, email, navigate, searchParams]);

  // When the workspace is scoped via `?as=<cid>` (Judy vs Rosemarie
  // on a shared email), the H1 + every downstream tab must show the
  // *canonical* client's name — not whichever sibling happens to be
  // the primary record on the email profile. Fall back to the profile
  // name only when no canonical scope is active.
  const rawClientName = profile?.client_name || "";
  const clientName = canonicalIdentity?.fullName || rawClientName;
  const rawClientPhone = profile?.client_phone || "";
  const clientPhone = canonicalIdentity?.phone || rawClientPhone;
  const nameAliases = profile?.name_aliases || [];

  // Called when ClientDetailsTab saves. Rebuild the display name from
  // the new first/middle/last so the header at the top of the page
  // + the global activeClient (used by Letters, SbN, etc.) update in
  // sync — no page refresh required.
  const handleDetailsSaved = (fresh) => {
    if (!fresh) return;
    const composed =
      fresh.full_name ||
      [fresh.first_name, fresh.middle_name, fresh.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();
    if (!composed) return;
    setProfile((p) => ({ ...(p || {}), client_name: composed,
                          client_phone: fresh.mobile || p?.client_phone }));
    // When scoped, the profile PUT mirrors identity fields onto the
    // canonical ``clients`` row — refresh the header identity + the
    // sibling switcher pill in place so the operator sees the rename
    // immediately without a hard page reload.
    if (canonicalIdentity) {
      setCanonicalIdentity((cur) => cur ? {
        ...cur,
        fullName: composed,
        phone: fresh.mobile || cur.phone,
      } : cur);
      setCanonicalRows((rows) => rows.map((r) =>
        r._id === canonicalIdentity.clientId
          ? { ...r, full_name: composed,
              first_name: fresh.first_name || r.first_name,
              last_name: fresh.last_name || r.last_name,
              phone: fresh.mobile || r.phone }
          : r,
      ));
      if (email) setActiveClient({ email, name: composed, canonical_client_id: canonicalIdentity.clientId });
    } else {
      setActiveClient({ email, name: composed });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-zinc-600 py-10">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading client…
      </div>
    );
  }

  return (
    <CanonicalClientContext.Provider value={canonicalIdentity}>
    <div className="space-y-6" data-testid="client-detail-page">
      <div>
        <button
          onClick={() => navigate("/clients")}
          className="text-xs text-zinc-600 hover:text-zinc-950 flex items-center gap-1 mb-3 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-1"
          data-testid="client-detail-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Clients
        </button>
        {canonicalIdentity && canonicalRows.length > 0 && (
          <SiblingSwitcher
            canonicalRows={canonicalRows}
            currentClientId={canonicalIdentity.clientId}
            email={email}
            onOpenRename={() => setRenameOpen(true)}
            onOpenSplit={() => setSplitOpen(true)}
          />
        )}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-600">
              {canonicalIdentity ? "Client — scoped record" : "Client"}
            </div>
            <h1 className="mt-2 font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-zinc-950 flex items-center gap-3 min-w-0">
              <User className="h-7 w-7 text-zinc-500 flex-shrink-0" />
              <span className="truncate" data-testid="client-detail-name">{clientName || email}</span>
            </h1>
            <div className="mt-2 flex items-center gap-4 text-sm text-zinc-700 flex-wrap font-mono-num">
              <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {email}</span>
              {clientPhone && (
                <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {clientPhone}</span>
              )}
            </div>
            {nameAliases.length > 0 && !canonicalIdentity && (
              <div
                className="mt-2 inline-flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-900"
                data-testid="client-name-aliases"
                title="Multiple people have filed submissions under this email — often a parent + child sharing an inbox. The Details tab drives the primary name."
              >
                <span className="font-semibold uppercase tracking-wider">Also on this email:</span>
                <span className="font-normal">
                  {nameAliases.slice(0, 3).join(", ")}
                  {nameAliases.length > 3 ? ` +${nameAliases.length - 3} more` : ""}
                </span>
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {editable && (
              <Button onClick={() => setSoapOpen(true)} data-testid="client-detail-record-btn">
                <Mic className="h-4 w-4 mr-1.5" /> Record consultation
              </Button>
            )}
            <Button variant="outline" onClick={() => setSmsOpen(true)} data-testid="client-detail-sms-btn">
              <MessageSquare className="h-4 w-4 mr-1.5" /> Send SMS
            </Button>
            <Button variant="outline" onClick={() => setNominateOpen(true)}
                    data-testid="client-detail-nominate-review-btn"
                    title="Flag this client for a Google review — manager still approves the draft">
              <Star className="h-4 w-4 mr-1.5 text-amber-500" fill="currentColor" /> Nominate for review
            </Button>
            {canonicalIdentity && (
              <Button
                variant="outline"
                onClick={() => setRenameOpen(true)}
                title="Correct this record's name — only affects the person currently in scope."
                data-testid="client-detail-rename-btn"
              >
                <Pencil className="h-4 w-4 mr-1.5" /> Rename
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setSplitOpen(true)}
              title="Create a second client record that shares this email (Judy/Rosemarie case)."
              data-testid="client-detail-split-btn"
              disabled={canonicalRows.length === 0}
            >
              <Split className="h-4 w-4 mr-1.5" />
              Split — add another person on this email
            </Button>
          </div>
        </div>
      </div>

      <div className="mb-3">
        <CriticalAlertsStrip email={email} />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <div className="sticky top-16 z-40 -mx-2 px-2 py-2 bg-white/90 backdrop-blur-xl" data-testid="client-detail-tabs-bar">
        <TabsList data-testid="client-detail-tabs" className="flex-wrap h-auto gap-0.5 justify-start w-full max-w-full overflow-x-auto thin-scroll">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="record" data-testid="tab-record">Health record</TabsTrigger>
          <TabsTrigger value="appointments" data-testid="tab-appointments">Appointments</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
          <TabsTrigger value="forms" data-testid="tab-forms">Forms</TabsTrigger>
          <TabsTrigger value="review" data-testid="tab-review">Onboarding review</TabsTrigger>
          <TabsTrigger value="assessments" data-testid="tab-assessments">Assessments</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="programs" data-testid="tab-programs">Programs</TabsTrigger>
          <TabsTrigger value="checkins" data-testid="tab-checkins">Progress Review</TabsTrigger>
          <TabsTrigger value="communications" data-testid="tab-communications">Communications</TabsTrigger>
        </TabsList>
        </div>
        <TabsContent value="overview" className="pt-4">
          <div className="mb-4">
            <ClinicalSummaryCard email={email} />
          </div>
          <OverviewTab profile={profile} email={email} />
          <div className="mt-6">
            <ClientDetailsTab email={email} onSaved={handleDetailsSaved} />
          </div>
        </TabsContent>
        <TabsContent value="record" className="pt-4">
          <HealthRecordTab email={email} clientName={clientName} onOpenTab={setTab} onOpenNewAssessment={openNewAssessment} />
        </TabsContent>
        <TabsContent value="appointments" className="pt-4">
          <AppointmentsTab email={email} />
        </TabsContent>
        <TabsContent value="notes" className="pt-4">
          <NotesTab email={email} clientName={clientName} refreshKey={soapRefresh} onRecord={() => setSoapOpen(true)} editable={editable} />
        </TabsContent>
        <TabsContent value="forms" className="pt-4">
          <FilesAndFormsTab email={email} defaultSection="forms" />
        </TabsContent>
        <TabsContent value="review" className="pt-4">
          <OnboardingReviewTab email={email} />
        </TabsContent>
        <TabsContent value="assessments" className="pt-4">
          <AssessmentsTab email={email} clientName={clientName} newAssessmentSignal={newAssessmentSignal} />
        </TabsContent>
        <TabsContent value="documents" className="pt-4">
          <FilesAndFormsTab email={email} defaultSection="files" />
        </TabsContent>
        <TabsContent value="programs" className="pt-4">
          <ExerciseProgramsPanel email={email} />
        </TabsContent>
        <TabsContent value="checkins" className="pt-4">
          <ClientCheckinsTab email={email} clientName={clientName} canonicalId={canonicalClientId} />
        </TabsContent>
        <TabsContent value="communications" className="pt-4">
          <CommunicationsTab
            email={email} clientName={clientName}
            phone={clientPhone}
            onSendSms={() => setSmsOpen(true)}
          />
        </TabsContent>
      </Tabs>

      {soapOpen && (
        <SoapSessionDialog
          open={soapOpen}
          onClose={() => { setSoapOpen(false); setSoapRefresh((n) => n + 1); }}
          clientEmail={email}
          clientName={clientName}
          onSaved={() => setSoapRefresh((n) => n + 1)}
        />
      )}
      <QuickSmsDialog
        open={smsOpen}
        onClose={() => setSmsOpen(false)}
        clientEmail={email}
        clientName={clientName}
        clientPhone={clientPhone}
      />
      <NominateForReviewDialog
        open={nominateOpen}
        onOpenChange={setNominateOpen}
        clientEmail={email}
        clientName={clientName}
      />
      <RenameCanonicalDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        client={
          canonicalRows.find((r) => r._id === canonicalIdentity?.clientId) || null
        }
        onSaved={(updated) => {
          setRenameOpen(false);
          // Refresh the sibling list + local name pill.
          setCanonicalRows((rows) =>
            rows.map((r) => (r._id === updated._id ? { ...r, ...updated } : r)),
          );
          setCanonicalIdentity((cur) =>
            cur ? { ...cur, fullName: updated.full_name || "" } : cur,
          );
          if (email && updated.full_name) {
            setActiveClient({ email, name: updated.full_name, canonical_client_id: canonicalClientId });
          }
          toast.success(`Renamed to ${updated.full_name || "(no name)"}`);
        }}
      />
      <SplitClientInlineDialog
        open={splitOpen}
        onOpenChange={setSplitOpen}
        sourceClient={
          canonicalRows.find((r) => r._id === canonicalIdentity?.clientId)
          || canonicalRows[0]
          || null
        }
        onSplit={(newRow) => {
          setSplitOpen(false);
          toast.success(
            `Created ${newRow.full_name || "new client"} on the same email — opening theirs.`,
          );
          // Route straight into the new sibling's workspace on the
          // same email — no detour through the canonical page.
          navigate(
            `/clients/${encodeURIComponent(email)}?as=${encodeURIComponent(newRow._id)}`,
          );
        }}
      />
    </div>
    </CanonicalClientContext.Provider>
  );
}


// Communications — single tab wrapper hosting Messages / Letters / SbN
// so the outer tab bar stays at the spec's 8 entries.
function CommunicationsTab({ email, clientName, phone, onSendSms }) {
  const [sub, setSub] = useState("messages");
  return (
    <div data-testid="communications-tab">
      <div className="inline-flex rounded-md border border-zinc-200 bg-zinc-50 p-0.5 mb-3">
        {[
          ["messages", "SMS"],
          ["letters", "Letters"],
          ["sbn", "SbN"],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setSub(k)}
            data-testid={`communications-sub-${k}`}
            className={
              "px-3 py-1 text-xs rounded transition " +
              (sub === k
                ? "bg-white text-zinc-900 shadow-sm"
                : "text-zinc-500 hover:text-zinc-900")
            }
          >
            {label}
          </button>
        ))}
      </div>
      {sub === "messages" && <MessagesTab phone={phone} onSend={onSendSms} />}
      {sub === "letters" && <LettersTab email={email} clientName={clientName} />}
      {sub === "sbn" && <SbnTab email={email} clientName={clientName} />}
    </div>
  );
}



function OverviewTab({ profile, email }) {
  const rows = [
    { label: "Submissions", value: profile?.total_submissions ?? 0 },
    { label: "Completed", value: profile?.submitted_count ?? 0 },
    { label: "Open", value: profile?.open_count ?? 0 },
    { label: "Last activity", value: fmtDT(profile?.last_activity_at) },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {rows.map((r) => (
          <Card key={r.label} className="p-4">
            <div className="text-2xs uppercase tracking-wider text-zinc-500">{r.label}</div>
            <div className="mt-1 text-2xl font-semibold font-mono-num text-zinc-950">{r.value}</div>
          </Card>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap">
        {profile?.has_pdq39 && (
          <Badge className="bg-violet-100 text-violet-800 border-violet-200 border">
            PDQ-SI {profile?.latest_pdq39_summary ?? "—"}
          </Badge>
        )}
        {profile?.has_onero && (
          <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 border">
            ONERO composite {profile?.latest_onero_composite ?? "—"}
          </Badge>
        )}
      </div>
      <ClientPainTrendCard email={email} />
    </div>
  );
}


function NotesTab({ email, clientName, refreshKey, onRecord, editable }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-600">
          Assessment sessions transcribed and structured into SOAP notes.
        </div>
        {editable && (
          <Button size="sm" onClick={onRecord} data-testid="notes-tab-record-btn">
            <Mic className="h-4 w-4 mr-1.5" /> Record consultation
          </Button>
        )}
      </div>
      <SoapNotesList email={email} refreshKey={refreshKey} />
    </div>
  );
}


function FilesAndFormsTab({ email }) {
  // Combined Files + Forms view. Files come from the health-record
  // attachments collection (Emergent Object Storage); forms are the
  // intake submissions. Two clearly-headed sections so operators can
  // scan quickly.
  const canonical = useContext(CanonicalClientContext);
  const [subs, setSubs] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback((silent) => {
    if (!email) return;
    if (silent !== true) setLoading(true);
    // Phase-2 scoped filter — when `?as=<cid>` is set, narrow both
    // intake submissions and attachments to just that canonical
    // client's rows. Attachments (`/health-record/*/attachments`)
    // doesn't yet accept the filter server-side, so it stays shared
    // until we roll out the same param there.
    const submissionParams = canonical?.clientId
      ? { canonical_client_id: canonical.clientId } : undefined;
    Promise.all([
      api.get(`/intake/clients/by-email/${encodeURIComponent(email)}/submissions`,
              submissionParams ? { params: submissionParams } : undefined)
        .then((r) => setSubs(r.data || []))
        .catch(() => setSubs([])),
      api.get(`/health-record/${encodeURIComponent(email)}/attachments`)
        .then((r) => setFiles(r.data?.attachments || r.data || []))
        .catch(() => setFiles([])),
    ]).finally(() => setLoading(false));
  }, [email, canonical?.clientId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-sm text-zinc-500 py-4">Loading…</div>;
  return (
    <div className="space-y-6">
      <ClientFilesSection email={email} files={files} onChanged={() => load(true)} />

      {/* Forms section */}
      <section className="space-y-2" data-testid="forms-section">
        <h3 className="text-xs uppercase tracking-wider text-zinc-500 font-semibold border-b border-zinc-200 pb-1.5">
          Forms ({subs.length})
        </h3>
        {subs.length === 0 ? (
          <div className="text-sm text-zinc-500 py-2">No form submissions yet.</div>
        ) : (
          <ul className="space-y-2">
            {subs.map((s) => (
              <li key={s.token} className="rounded-lg border border-zinc-200 p-3 flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={
                      s.status === "submitted"
                        ? "bg-emerald-100 text-emerald-800 border-emerald-200 border text-2xs uppercase"
                        : "bg-amber-100 text-amber-800 border-amber-200 border text-2xs uppercase"
                    }>{s.status}</Badge>
                    <span className="font-medium text-zinc-900 text-sm">{s.template_name || "Form"}</span>
                    {s.appointment_type && (
                      <span className="text-xs text-zinc-500">· {s.appointment_type}</span>
                    )}
                  </div>
                  <div className="text-2xs text-zinc-500 mt-1 font-mono-num">
                    sent {fmtDT(s.created_at)}
                    {s.submitted_at && ` · submitted ${fmtDT(s.submitted_at)}`}
                  </div>
                </div>
                {s.token && (
                  s.status === "submitted" ? (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => downloadCompactOnboardingPdf(s.token)
                          .catch((e) => toast.error("Could not download compact PDF", { description: e.message }))}
                        className="text-xs text-zinc-600 hover:text-zinc-900 flex items-center gap-1 whitespace-nowrap"
                        data-testid={`client-form-compact-pdf-${s.token}`}
                        title="Download compact PDF"
                      >
                        <FileText className="h-3 w-3" /> Compact PDF
                      </button>
                      <a
                        href={`/intake-forms?view=${encodeURIComponent(s.token)}`}
                        className="text-xs text-zinc-600 hover:text-zinc-900 flex items-center gap-1 whitespace-nowrap"
                        data-testid={`client-form-view-${s.token}`}
                        title="View the answers this client submitted"
                      >
                        View answers <ArrowRight className="h-3 w-3" />
                      </a>
                    </div>
                  ) : (
                    <a
                      href={`/intake/${s.token}`}
                      target="_blank" rel="noreferrer"
                      className="text-xs text-zinc-600 hover:text-zinc-900 flex items-center gap-1 whitespace-nowrap"
                      data-testid={`client-form-open-${s.token}`}
                      title="Open the client-facing form (still pending)"
                    >
                      Open form <ExternalLink className="h-3 w-3" />
                    </a>
                  )
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}


function MessagesTab({ phone, onSend }) {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!phone) return;
    setLoading(true);
    api.get(`/sms/conversation/${encodeURIComponent(phone)}`)
      .then((r) => {
        // Endpoint shape: { phone, count, outbound_count, inbound_count, items: [...] }
        const items = Array.isArray(r.data?.items) ? r.data.items : [];
        // Newest last in a chat feel — server already sorts oldest → newest.
        setMsgs(items);
      })
      .catch(() => setMsgs([]))
      .finally(() => setLoading(false));
  }, [phone]);

  if (!phone) {
    return (
      <div className="text-sm text-zinc-500 rounded-md border border-dashed border-zinc-200 p-6 text-center">
        No phone number on file — cannot show SMS thread.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-600">SMS thread with <span className="font-mono-num">{phone}</span></div>
        <Button size="sm" onClick={onSend} data-testid="messages-tab-send-btn">
          <Send className="h-4 w-4 mr-1.5" /> New SMS
        </Button>
      </div>
      {loading ? (
        <div className="text-sm text-zinc-500 py-4">Loading…</div>
      ) : msgs.length === 0 ? (
        <div className="text-sm text-zinc-500 rounded-md border border-dashed border-zinc-200 p-6 text-center">
          No messages yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {msgs.slice(-50).map((m, i) => {
            const isOut = m.direction === "out";
            return (
              <li
                key={i}
                className={`rounded-lg border p-3 text-sm ${
                  isOut
                    ? "bg-zinc-50 border-zinc-200"
                    : "bg-emerald-50 border-emerald-200"
                }`}
              >
                <div className="flex items-center justify-between text-2xs text-zinc-500 font-mono-num mb-1">
                  <span>
                    {isOut ? "SENT" : "RECEIVED"}
                    {isOut && m.status && m.status !== "SUCCESS" && (
                      <span className="ml-2 text-red-700">{m.status}</span>
                    )}
                    {!isOut && m.intent && (
                      <span className="ml-2 text-emerald-700">{m.intent}</span>
                    )}
                  </span>
                  <span>{fmtDT(m.ts)}</span>
                </div>
                <div className="whitespace-pre-wrap text-zinc-900">{m.body || ""}</div>
                {m.error_text && (
                  <div className="mt-1 text-2xs text-red-700">{m.error_text}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


function AppointmentsTab({ email }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!email) return;
    setLoading(true);
    // 90 days lookback + 90 days lookahead — enough for most clinical timelines
    const today = new Date();
    const past = new Date(today.getTime() - 90 * 86400_000);
    const future = new Date(today.getTime() + 90 * 86400_000);
    const iso = (d) => melDate(d);
    api.get("/appointments", { params: {
      email, minDate: iso(past), maxDate: iso(future), max: 200,
      // The Appointments tab only renders type, date, calendar and
      // intake-form status — none of the price fields. Skipping the
      // price enrichment cuts this call from ~30 s to ~1 s.
      enrich_price: false,
    } })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [email]);

  if (loading) return <div className="text-sm text-zinc-500 py-4">Loading…</div>;
  if (!rows.length) return <div className="text-sm text-zinc-500 py-4">No appointments in the last / next 90 days.</div>;
  return (
    <ul className="space-y-2">
      {rows.map((a) => (
        <li key={a.id} className="rounded-lg border border-zinc-200 p-3 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-zinc-900 text-sm">{a.type || "Appointment"}</span>
              {a.canceled && <Badge className="bg-red-100 text-red-800 border-red-200 border text-2xs uppercase">Cancelled</Badge>}
            </div>
            <div className="text-2xs text-zinc-500 mt-0.5 font-mono-num">
              {fmtDT(a.datetime)} · {a.calendar || a.calendarID}
            </div>
            {a.intake_form && (
              <div className="text-2xs mt-1">
                Intake: <span className={a.intake_form.status === "submitted" ? "text-emerald-700" : "text-amber-700"}>
                  {a.intake_form.status}
                </span>
              </div>
            )}
          </div>
          <div className="text-2xs text-zinc-500 font-mono-num">#{a.id}</div>
        </li>
      ))}
    </ul>
  );
}


// ---------------------------------------------------------------------------
// LettersTab — inline history of clinical letters for this client, with
// quick "New letter" (pre-fills the client) and "Duplicate" row actions.
// Only pulls the last 50 rows via /api/letters?client_email=... — the
// full manager is at /letters if the operator needs deeper filtering.
// ---------------------------------------------------------------------------
const STATUS_TONE = {
  draft:    "bg-zinc-100 text-zinc-700 border-zinc-200",
  ready:    "bg-blue-50 text-blue-800 border-blue-200",
  sent:     "bg-emerald-50 text-emerald-800 border-emerald-200",
  archived: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

function LettersTab({ email, clientName }) {
  const navigate = useNavigate();
  const canonical = useContext(CanonicalClientContext);
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    try {
      const params = { client_email: email, limit: 50 };
      // Phase-2 scoped filter — narrow to just this canonical
      // client's letters (Judy vs Rosemarie).
      if (canonical?.clientId) params.canonical_client_id = canonical.clientId;
      const r = await api.get("/letters", { params });
      setLetters(r.data.letters || []);
    } catch (e) {
      toast.error("Failed to load letters", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [email, canonical?.clientId]);

  async function duplicate(id) {
    try {
      const r = await api.post(`/letters/${id}/duplicate`);
      toast.success("Duplicated — opening the new draft");
      navigate(`/letters/${r.data.id}`);
    } catch (e) {
      toast.error("Duplicate failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  function goToNew() {
    // Pre-fill client via query string — LetterCompose reads it on mount.
    const params = new URLSearchParams({
      client_email: email,
      client_name: clientName || "",
    });
    // When the workspace is scoped to a specific sibling, stamp the
    // draft with their canonical_client_id so the letter belongs to
    // Judy (not Rosemarie) end-to-end — the letters list filter, the
    // profile-override merge on export, and the outbound CC lookup
    // all key off this value.
    if (canonical?.clientId) params.set("canonical_client_id", canonical.clientId);
    navigate(`/letters/new?${params.toString()}`);
  }

  return (
    <div data-testid="letters-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <p className="text-xs text-zinc-500">
          Every letter drafted for this client, most recent first. Duplicate
          any past letter to fast-track next quarter&apos;s update.
        </p>
        <Button
          onClick={goToNew}
          className="bg-zinc-950 hover:bg-zinc-800 text-white"
          data-testid="client-letters-new-btn"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New letter
        </Button>
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading letters…
        </div>
      ) : letters.length === 0 ? (
        <div className="py-10 text-center border border-dashed border-zinc-200 rounded-md">
          <Mail className="h-8 w-8 mx-auto text-zinc-300" />
          <p className="mt-2 text-sm text-zinc-600">
            No letters yet for this client. Tap <b>New letter</b> to draft one.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100 rounded-md border border-zinc-200" data-testid="client-letters-list">
          {letters.map((l) => (
            <li
              key={l.id}
              className="p-3 flex items-center gap-3 flex-wrap hover:bg-zinc-50 transition-colors"
              data-testid={`client-letter-row-${l.id}`}
            >
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-zinc-900 text-sm">
                    {l.subject || (l.template_id === "ndis_treatment_report" ? "NDIS treatment report" : "GP care plan update")}
                  </span>
                  <span
                    className={`text-2xs px-2 py-0.5 rounded-full border capitalize ${STATUS_TONE[l.status] || STATUS_TONE.draft}`}
                  >
                    {l.status}
                  </span>
                </div>
                <div className="text-2xs text-zinc-500 mt-0.5 font-mono-num">
                  {l.template_id === "ndis_treatment_report" ? "NDIS" : "GP"}
                  {l.recipient_snapshot?.last_name && (
                    <> · {l.recipient_snapshot.title} {l.recipient_snapshot.first_name} {l.recipient_snapshot.last_name}</>
                  )}
                  {" · updated "}
                  {(l.updated_at || "").slice(0, 16).replace("T", " ")}
                  {l.sent_at && (
                    <> · sent {l.sent_at.slice(0, 10)}</>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  size="sm" variant="ghost" className="h-7 text-xs"
                  onClick={() => duplicate(l.id)}
                  data-testid={`client-letter-dup-${l.id}`}
                  title="Clone as a new draft"
                >
                  <Copy className="h-3 w-3 mr-1" /> Duplicate
                </Button>
                <Button
                  size="sm" variant="ghost" className="h-7 text-xs"
                  onClick={() => navigate(`/letters/${l.id}`)}
                  data-testid={`client-letter-open-${l.id}`}
                >
                  Open <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}



/**
 * SbnTab — surfaces Strength by Numbers data for this local client.
 *
 *  - If not linked: inline SbN client search + one-click link
 *  - If linked:     latest session summary + link out to /sbn/clients/:id
 */
function SbnTab({ email, clientName }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState({ configured: null });
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  // search state (unlinked)
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [suggestionReason, setSuggestionReason] = useState("");
  const [autoSearching, setAutoSearching] = useState(false);

  async function loadLink() {
    setLoading(true);
    try {
      const [st, lk] = await Promise.all([
        api.get("/sbn/status"),
        api.get(`/sbn/links/by-email/${encodeURIComponent(email)}`),
      ]);
      setStatus(st.data);
      const linkDoc = lk.data?.link || null;
      setLink(linkDoc);
      if (linkDoc?.sbn_client_id) {
        const s = await api.get(
          `/sbn/clients/${encodeURIComponent(linkDoc.sbn_client_id)}/sessions`,
          { params: { take: 10, skip: 0 } },
        );
        setSessions(s.data?.sessions || []);
        setSummary(s.data?.sessions?.[0] || null);
      } else {
        setSessions([]); setSummary(null);
      }
    } catch (e) {
      toast.error("SbN load failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }
  useEffect(() => { loadLink(); }, [email]);

  // Auto-detect a matching SbN client when unlinked. Strategy:
  //   1. Search by the local email (email + local-part fallback)
  //      → any SbN client whose email matches exactly wins.
  //   2. Otherwise, search by name and only propose if there's exactly
  //      one match — never guess between multiple people.
  useEffect(() => {
    if (loading) return;
    if (link) { setSuggestion(null); setSuggestionReason(""); return; }
    if (!status.configured) return;
    let cancelled = false;
    setAutoSearching(true);
    (async () => {
      try {
        const tries = [];
        // 1a. Search using the full email — some SbN accounts index it.
        if (email) tries.push({ q: email, reason: "email-match" });
        // 1b. Search using just the local-part of the email (before @).
        const local = (email || "").split("@")[0].replace(/[._-]+/g, " ").trim();
        if (local && local.length >= 2) tries.push({ q: local, reason: "email-match" });
        // 2. Search by client name.
        const trimmedName = (clientName || "").trim();
        if (trimmedName.length >= 2) tries.push({ q: trimmedName, reason: "name-match" });

        for (const attempt of tries) {
          if (cancelled) return;
          try {
            const r = await api.get("/sbn/clients", {
              params: { q: attempt.q, take: 15 },
            });
            const candidates = r.data?.clients || [];
            if (cancelled) return;

            if (attempt.reason === "email-match") {
              // Prefer exact email match — unambiguous.
              const exact = candidates.find(
                (c) => (c.email || "").toLowerCase() === (email || "").toLowerCase(),
              );
              if (exact) {
                setSuggestion(exact);
                setSuggestionReason("email-match");
                return;
              }
            }

            if (attempt.reason === "name-match" && candidates.length === 1) {
              // Only one name match — safe to suggest but flag as fuzzy.
              setSuggestion(candidates[0]);
              setSuggestionReason("name-match");
              return;
            }
          } catch { /* try next strategy */ }
        }
        // Nothing matched.
        setSuggestion(null);
        setSuggestionReason("");
      } finally {
        if (!cancelled) setAutoSearching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loading, link, status.configured, email, clientName]);

  // Live search when unlinked
  useEffect(() => {
    if (link || !q || q.trim().length < 2) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/sbn/clients", { params: { q: q.trim(), take: 15 } });
        if (!cancelled) setResults(r.data?.clients || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally { if (!cancelled) setSearching(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, link]);

  async function linkToSbn(sbn) {
    setLinkBusy(true);
    try {
      const nm = [sbn.first_name, sbn.last_name].filter(Boolean).join(" ");
      await api.post("/sbn/links", {
        email,
        sbn_client_id: sbn.client_id,
        sbn_client_name: nm || null,
      });
      toast.success("Linked to SbN client");
      setQ(""); setResults([]);
      await loadLink();
    } catch (e) {
      toast.error("Link failed", { description: e?.response?.data?.detail || e.message });
    } finally { setLinkBusy(false); }
  }

  async function unlink() {
    if (!window.confirm("Remove the link to this SbN client?")) return;
    try {
      await api.delete(`/sbn/links/by-email/${encodeURIComponent(email)}`);
      toast.success("Unlinked");
      await loadLink();
    } catch (e) {
      toast.error("Unlink failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  if (loading) {
    return (
      <div className="text-sm text-zinc-500 py-4 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading SbN link…
      </div>
    );
  }

  if (status.configured === false) {
    return (
      <Card className="p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-900">
        <div className="text-sm">
          Strength by Numbers is not configured. Save your API key in{" "}
          <button
            className="underline"
            onClick={() => navigate("/settings")}
            data-testid="sbn-tab-goto-settings"
          >Settings</button>{" "}to review assessment data here.
        </div>
      </Card>
    );
  }

  // Not linked yet → search UI
  if (!link) {
    const nameSeed = clientName || "";
    const suggestionName = suggestion
      ? ([suggestion.first_name, suggestion.last_name].filter(Boolean).join(" ") || "—")
      : "";
    return (
      <div className="space-y-4" data-testid="client-sbn-tab-unlinked">
        {suggestion && (
          <Card
            className="p-4 rounded-lg border border-emerald-200 bg-emerald-50"
            data-testid="client-sbn-suggestion"
          >
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 grid place-items-center rounded-md bg-emerald-600 text-white flex-shrink-0">
                <Link2 className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="text-2xs uppercase tracking-[0.2em] text-emerald-900 font-semibold flex items-center gap-2">
                  {suggestionReason === "email-match"
                    ? "Auto-matched by email"
                    : "Only match by name"}
                </div>
                <div className="mt-0.5 text-sm text-emerald-950">
                  <span className="font-semibold">{suggestionName}</span>
                  {suggestion.email && (
                    <span className="text-emerald-800/80 font-mono-num"> · {suggestion.email}</span>
                  )}
                </div>
                <div className="text-2xs text-emerald-800/80 mt-0.5">
                  {suggestion.sessionCount ?? 0} session
                  {suggestion.sessionCount === 1 ? "" : "s"} on file
                  {suggestionReason === "name-match" && " · confirm this is the right person before linking"}
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Button
                  size="sm"
                  onClick={() => linkToSbn(suggestion)}
                  disabled={linkBusy}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="client-sbn-suggest-link"
                >
                  {linkBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Link2 className="h-3.5 w-3.5 mr-1" />}
                  Link now
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setSuggestion(null); setSuggestionReason(""); }}
                  className="h-7 text-xs text-emerald-900 hover:bg-emerald-100"
                  data-testid="client-sbn-suggest-dismiss"
                >
                  Not this one
                </Button>
              </div>
            </div>
          </Card>
        )}

        <Card className="p-4 rounded-lg border border-zinc-200">
          <div className="text-sm text-zinc-700">
            {suggestion
              ? "Prefer a different client? Search SbN manually below."
              : "This client isn't linked to a Strength by Numbers profile yet. Search SbN by name to bind them — future SOAP notes will then auto-pull dynamometry results for this client."}
          </div>
          <div className="mt-3 relative max-w-lg">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q || nameSeed}
              onChange={(e) => setQ(e.target.value)}
              placeholder={nameSeed || "Search SbN by name (min. 2 chars)…"}
              className="pl-9"
              data-testid="client-sbn-search"
              autoFocus={!nameSeed}
            />
          </div>
          {(searching || autoSearching) && (
            <div className="mt-2 text-xs text-zinc-500 flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {autoSearching && !searching ? "Checking SbN for a match…" : "Searching SbN…"}
            </div>
          )}
          {results.length > 0 && (
            <ul className="mt-3 divide-y border border-zinc-200 rounded-md" data-testid="client-sbn-results">
              {results.map((c) => {
                const nm = [c.first_name, c.last_name].filter(Boolean).join(" ") || "—";
                return (
                  <li key={c.client_id} className="flex items-center justify-between gap-2 px-3 py-2">
                    <div>
                      <div className="text-sm font-medium text-zinc-900">{nm}</div>
                      <div className="text-2xs text-zinc-500 font-mono-num">
                        {c.email || "no email on file"} · {c.sessionCount ?? 0} sessions
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => linkToSbn(c)}
                      disabled={linkBusy}
                      className="bg-zinc-950 hover:bg-zinc-800 text-white"
                      data-testid={`client-sbn-link-${c.client_id}`}
                    >
                      <Link2 className="h-3.5 w-3.5 mr-1" /> Link
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    );
  }

  // Linked view
  return (
    <div className="space-y-4" data-testid="client-sbn-tab-linked">
      <Card className="p-4 rounded-lg border border-zinc-200">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">
              Linked SbN client
            </div>
            <div className="mt-1 font-semibold text-zinc-950 flex items-center gap-2">
              <Activity className="h-4 w-4 text-zinc-400" />
              {link.sbn_client_name || link.sbn_client_id}
            </div>
            <div className="text-2xs text-zinc-500 font-mono-num mt-0.5">
              linked {fmtDT(link.linked_at)}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/sbn/clients/${encodeURIComponent(link.sbn_client_id)}`)}
              data-testid="client-sbn-open-full"
            >
              Open full review <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
            <Button
              variant="outline"
              onClick={unlink}
              className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
              data-testid="client-sbn-unlink"
            >
              <Link2Off className="h-3.5 w-3.5 mr-1" /> Unlink
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-4 rounded-lg border border-zinc-200">
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">
          Recent sessions
        </div>
        {sessions.length === 0 ? (
          <div className="text-sm text-zinc-500 py-3">No sessions recorded in SbN yet.</div>
        ) : (
          <ul className="divide-y">
            {sessions.slice(0, 5).map((s) => {
              const testCount = (s.tests || []).length;
              const metricCount = (s.tests || [])
                .reduce((acc, t) => acc + (t.results || []).length, 0);
              return (
                <li key={s.session_id} className="py-2.5 flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-mono-num text-zinc-800">{fmtDT(s.time)}</div>
                    <div className="text-2xs text-zinc-500">
                      {testCount} test{testCount === 1 ? "" : "s"} · {metricCount} metric{metricCount === 1 ? "" : "s"}
                      {s.notes ? ` · ${s.notes.slice(0, 60)}${s.notes.length > 60 ? "…" : ""}` : ""}
                    </div>
                  </div>
                  <Button
                    size="sm" variant="ghost" className="h-8 text-xs"
                    onClick={() => navigate(`/sbn/clients/${encodeURIComponent(link.sbn_client_id)}`)}
                    data-testid={`client-sbn-session-${s.session_id}`}
                  >
                    View <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}


function AssessmentsTab({ email, clientName, newAssessmentSignal = 0 }) {
  const navigate = useNavigate();
  const canonical = useContext(CanonicalClientContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [selectedModules, setSelectedModules] = useState(["core"]);
  const [creating, setCreating] = useState(false);
  const clientId = `acuity:${email}`;
  const [intakeToken, setIntakeToken] = useState(null);
  const measurementPoints = useMeasurementPoints(clientId, canonical?.clientId);
  const [autoPick, setAutoPick] = useState(null);              // {module_keys, pathway_key, source, appointment_type_name, explanation}
  const [manuallyEdited, setManuallyEdited] = useState(false);
  const [intakeSummary, setIntakeSummary] = useState(null);    // {found, highlights[], template_name, ...}

  async function load() {
    setLoading(true);
    try {
      // Phase-2 scoped filter — when the parent hub is in `?as=<cid>`
      // mode, narrow to just that canonical client's assessments.
      const params = { client_id: clientId };
      if (canonical?.clientId) params.canonical_client_id = canonical.clientId;
      const r = await api.get("/assessments", { params });
      setRows(r.data || []);
    } catch (e) {
      toast.error("Could not load assessments", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }

  useEffect(() => {
    load();
    api.get("/assessments/modules").then((r) => setModules(r.data?.modules || [])).catch(() => {});
    // Latest-intake highlight card — small opinionated summary of the
    // client's most recent onboarding submission so the clinician has
    // instant context before opening an assessment.
    api.get("/intake/latest-summary", { params: { client_email: email } })
      .then((r) => setIntakeSummary(r.data))
      .catch(() => setIntakeSummary({ found: false }));
    // Auto-picker: resolve modules from the client's last Acuity appointment type.
    api.get("/assessments/suggest-modules", { params: { client_email: email } })
      .then((r) => {
        const d = r.data || {};
        setAutoPick(d);
        if (Array.isArray(d.module_keys) && d.module_keys.length) {
          setSelectedModules(d.module_keys);
        }
      })
      .catch(() => setAutoPick({ source: "no_data", module_keys: ["core"], pathway_key: "core" }));
  }, [email]);

  // Open the "New assessment" dialog whenever the parent bumps
  // `newAssessmentSignal` (e.g. from the HealthRecordTab quick-action
  // button). We deliberately skip the initial 0 so the dialog doesn't
  // auto-open on first mount.
  useEffect(() => {
    if (newAssessmentSignal > 0) setShowNew(true);
  }, [newAssessmentSignal]);

  function toggleModule(key) {
    if (key === "core") return;
    setManuallyEdited(true);
    setSelectedModules((cur) => cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]);
  }

  function resetToAutoPick() {
    if (autoPick && Array.isArray(autoPick.module_keys) && autoPick.module_keys.length) {
      setSelectedModules(autoPick.module_keys);
      setManuallyEdited(false);
    }
  }

  async function createNew() {
    setCreating(true);
    try {
      const r = await api.post("/assessments", {
        client_id: clientId,
        client_name: clientName || email,
        client_email: email,
        module_keys: selectedModules,
        intake_submission_token: intakeToken || undefined,
        // Phase-2 scoped writes — stamp so the new assessment shows
        // up on the canonical page's Assessments list (Judy vs Rosemarie).
        canonical_client_id: canonical?.clientId || null,
      });
      toast.success("Assessment started");
      navigate(`/assessments/${r.data.id}`);
    } catch (e) {
      toast.error("Could not create assessment", { description: e?.response?.data?.detail || e.message });
    } finally { setCreating(false); }
  }

  return (
    <Card className="p-4 border border-zinc-200" data-testid="client-assessments-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Smart clinical assessment</div>
          <div className="text-sm text-zinc-600 mt-0.5">Practitioner assessments for {clientName || email}.</div>
        </div>
        <div className="flex items-center gap-2">
          <PrintFormButton
            testid="client-print-form-btn"
            clientEmail={email}
            clientName={clientName}
            canonicalClientId={canonical?.clientId}
            defaultTemplateKey={(autoPick?.module_keys || []).find((k) => k !== "core")}
          />
          <Button
            onClick={() => setShowNew(true)}
            size="sm"
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="client-new-assessment"
          >
            <Plus className="h-4 w-4 mr-1.5" /> New assessment
          </Button>
        </div>
      </div>

      {intakeSummary?.found && (
        <IntakeSummaryCard summary={intakeSummary} />
      )}

      {!loading && rows.length > 0 && (
        <div className="mt-3 space-y-3">
          <MeasurementProgressCard points={measurementPoints} />
          <MeasurementTrend points={measurementPoints} />
        </div>
      )}

      <div className="mt-3 space-y-2">
        {loading && <div className="text-sm text-zinc-500">Loading…</div>}
        {!loading && rows.length === 0 && <div className="text-sm text-zinc-500">No assessments yet.</div>}
        {rows.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => navigate(`/assessments/${r.id}`)}
            className="w-full text-left p-3 rounded border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50"
            data-testid={`client-assessment-row-${r.id}`}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={r.status === "finalised" ? "default" : "outline"}>{r.status}</Badge>
                  {(r.modules || []).filter((m) => !m.hidden).map((m) => (
                    <Badge key={m.key} variant="outline" className="text-2xs">{m.key}</Badge>
                  ))}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  Started {new Date(r.started_at).toLocaleString()} · {r.practitioner_name}
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-zinc-400" />
            </div>
          </button>
        ))}
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent
          className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0"
          data-testid="client-new-assessment-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-200 shrink-0">
            <DialogTitle>New assessment for {clientName || email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1 min-h-0">
          <IntakeFormPicker email={email} name={clientName} value={intakeToken} onChange={setIntakeToken} />
          {autoPick && ["map_id", "map_name", "seed_guess_from_last_appt", "seed_guess_from_explicit", "onboarding_form"].includes(autoPick.source) && (
            <div
              className="p-3 rounded border border-sky-200 bg-sky-50 text-sm text-sky-900 flex items-start justify-between gap-3"
              data-testid="assessment-auto-pick-banner"
            >
              <div>
                <b>Auto-picked</b> — {autoPick.explanation}
              </div>
              {manuallyEdited && (
                <button
                  type="button"
                  onClick={resetToAutoPick}
                  className="text-sky-700 hover:text-sky-900 underline text-xs whitespace-nowrap"
                  data-testid="assessment-auto-pick-reset"
                >
                  Reset to auto
                </button>
              )}
            </div>
          )}
          {autoPick && autoPick.source === "unmapped_last_appt" && (
            <div
              className="p-3 rounded border border-amber-200 bg-amber-50 text-sm text-amber-900"
              data-testid="assessment-auto-pick-unmapped"
            >
              Client's last appointment (<b>{autoPick.appointment_type_name}</b>) has no
              pathway mapping yet. Pick modules manually — add a mapping in
              <b> Settings → Clinical → Assessment templates</b> to speed this up next time.
            </div>
          )}
          <div className="grid gap-2">
            {modules.map((m) => {
              const selected = selectedModules.includes(m.key);
              return (
                <button
                  type="button"
                  key={m.key}
                  onClick={() => toggleModule(m.key)}
                  disabled={m.always_on}
                  className={`text-left p-3 rounded border ${
                    selected ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"
                  } ${m.always_on ? "opacity-70 cursor-not-allowed" : ""}`}
                  data-testid={`client-new-module-${m.key}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-zinc-950">{m.label}</div>
                    {m.always_on && <Badge className="text-2xs">always on</Badge>}
                  </div>
                  <div className="text-xs text-zinc-600 mt-0.5">{m.description}</div>
                </button>
              );
            })}
          </div>
          </div>
          <DialogFooter className="px-6 py-3 border-t border-zinc-200 bg-white shrink-0">
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button
              onClick={createNew}
              disabled={creating}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="client-new-assessment-submit"
            >
              {creating ? "Starting…" : "Start assessment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


/**
 * Small at-a-glance card that surfaces the highlights of the client's
 * most recent completed intake submission (safety-critical answers
 * first, then goals/meds/other canonical fields, capped at 8). Renders
 * only when `summary.found === true`.
 *
 * Read-only — clicking an item just scrolls to the reviewer tab; the
 * clinician verifies / amends via the Onboarding Review workflow.
 */
function IntakeSummaryCard({ summary }) {
  const highlights = summary?.highlights || [];
  if (highlights.length === 0) return null;
  const submitted = summary?.submitted_at ? new Date(summary.submitted_at) : null;
  return (
    <div
      className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3"
      data-testid="intake-summary-card"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-indigo-900">
          <ClipboardList className="h-3.5 w-3.5" />
          Latest completed intake
          {summary.template_version && (
            <span className="text-2xs px-1 rounded border border-indigo-300 bg-white text-indigo-800 font-mono-num normal-case tracking-normal">
              v{summary.template_version}
            </span>
          )}
        </div>
        <div className="text-2xs text-indigo-800/80">
          {summary.template_name || "Intake"}
          {submitted && (
            <> · submitted {submitted.toLocaleDateString()}</>
          )}
          {summary.safety_critical_count > 0 && (
            <> · <span className="text-rose-800 font-medium">
              {summary.safety_critical_count} safety-critical
            </span></>
          )}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-1.5">
        {highlights.map((h) => (
          <div
            key={h.canonical_key}
            className={`text-xs rounded border px-2 py-1.5 ${
              h.safety_critical
                ? "border-rose-300 bg-rose-50"
                : "border-indigo-200 bg-white"
            }`}
            data-testid={`intake-summary-item-${h.canonical_key}`}
          >
            <div className="flex items-center gap-1 mb-0.5">
              {h.safety_critical && (
                <AlertTriangle className="h-3 w-3 text-rose-700 shrink-0" />
              )}
              <span className={`text-2xs uppercase tracking-wider font-semibold ${
                h.safety_critical ? "text-rose-900" : "text-indigo-900"
              }`}>
                {h.label}
              </span>
            </div>
            <div className={`text-xs ${
              h.safety_critical ? "text-rose-950" : "text-zinc-900"
            }`}>
              {h.value}
            </div>
          </div>
        ))}
      </div>
      <div className="text-2xs text-indigo-700 mt-2 flex items-center gap-1">
        <Wand2 className="h-3 w-3" />
        These values pre-populate any new assessment. Verify or amend on the
        Onboarding review tab.
      </div>
    </div>
  );
}




/**
 * SiblingSwitcher — segmented pills for shared-email families. Shows
 * every canonical row on this email as a clickable chip so the
 * operator can flip between "Judy" and "Rosemarie" in one click,
 * without leaving the workspace.
 */
function SiblingSwitcher({ canonicalRows, currentClientId, email, onOpenRename, onOpenSplit }) {
  const navigate = useNavigate();
  if (canonicalRows.length === 0) return null;
  return (
    <div
      className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-2"
      data-testid="sibling-switcher"
    >
      <span className="text-2xs font-semibold uppercase tracking-wider text-indigo-950">
        {canonicalRows.length === 1 ? "This email" : `${canonicalRows.length} people on this email`}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {canonicalRows.map((r) => {
          const active = r._id === currentClientId;
          return (
            <button
              key={r._id}
              type="button"
              onClick={() => {
                if (active) return;
                navigate(
                  `/clients/${encodeURIComponent(email)}?as=${encodeURIComponent(r._id)}`,
                );
              }}
              className={
                "px-2.5 py-1 rounded-full text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 " +
                (active
                  ? "bg-indigo-900 text-white cursor-default"
                  : "bg-white text-indigo-950 border border-indigo-200 hover:bg-indigo-100")
              }
              data-testid={`sibling-switch-${r._id}`}
              aria-current={active ? "true" : undefined}
            >
              {r.full_name || "(no name)"}
              {active && <span className="ml-1 opacity-70">·now editing</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}


/**
 * RenameCanonicalDialog — inline replacement for the old canonical
 * page's rename tool. Patches ``first_name`` / ``last_name`` on the
 * scoped canonical row so a mislabelled backfill can be corrected
 * without leaving the workspace.
 */
function RenameCanonicalDialog({ open, onOpenChange, client, onSaved }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setFirstName(""); setLastName(""); return; }
    setFirstName(client?.first_name || "");
    setLastName(client?.last_name || "");
  }, [open, client]);

  const unchanged =
    firstName.trim() === (client?.first_name || "").trim() &&
    lastName.trim() === (client?.last_name || "").trim();

  async function submit() {
    if (!firstName.trim() && !lastName.trim()) {
      toast.error("At least a first or last name is required");
      return;
    }
    if (unchanged) { onOpenChange(false); return; }
    if (!client?._id) return;
    setSaving(true);
    try {
      const r = await api.patch(
        `/clients/id/${encodeURIComponent(client._id)}`,
        { first_name: firstName.trim(), last_name: lastName.trim() },
      );
      onSaved?.(r.data);
    } catch (e) {
      toast.error("Rename failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="client-rename-dialog">
        <DialogHeader>
          <DialogTitle>Rename this record</DialogTitle>
          <DialogDescription>
            Only the identity fields on this canonical row change. Other people on the same email are untouched.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div>
            <Label className="text-xs">First name</Label>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                   autoFocus data-testid="rename-first-name" />
          </div>
          <div>
            <Label className="text-xs">Last name</Label>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)}
                   data-testid="rename-last-name" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || unchanged} data-testid="rename-submit">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * SplitClientInlineDialog — creates a second canonical client that
 * shares this email. Also lets the operator rename the source row on
 * the same save (needed when a backfill mislabelled it, otherwise
 * both rows end up with the same name → navigation loop).
 */
function SplitClientInlineDialog({ open, onOpenChange, sourceClient, onSplit }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [srcFirstName, setSrcFirstName] = useState("");
  const [srcLastName, setSrcLastName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setFirstName(""); setLastName(""); setPhone(""); setDob("");
      setSrcFirstName(""); setSrcLastName("");
      return;
    }
    setSrcFirstName(sourceClient?.first_name || "");
    setSrcLastName(sourceClient?.last_name || "");
  }, [open, sourceClient]);

  const newFull = `${firstName.trim()} ${lastName.trim()}`.trim().toLowerCase();
  const srcFull = `${srcFirstName.trim()} ${srcLastName.trim()}`.trim().toLowerCase();
  const willCollide = !!newFull && !!srcFull && newFull === srcFull;
  const srcChanged =
    (srcFirstName.trim() !== (sourceClient?.first_name || "").trim()) ||
    (srcLastName.trim() !== (sourceClient?.last_name || "").trim());

  async function submit() {
    if (!firstName.trim()) {
      toast.error("New person's first name is required");
      return;
    }
    if (!srcFirstName.trim() && !srcLastName.trim()) {
      toast.error("Current record must have at least a first or last name");
      return;
    }
    if (willCollide) {
      toast.error("Names must differ", {
        description: "Give each person a distinct name so they're navigable.",
      });
      return;
    }
    if (!sourceClient?._id) return;
    setSaving(true);
    try {
      if (srcChanged) {
        await api.patch(`/clients/id/${encodeURIComponent(sourceClient._id)}`, {
          first_name: srcFirstName.trim(),
          last_name: srcLastName.trim(),
        });
      }
      const r = await api.post(
        `/clients/id/${encodeURIComponent(sourceClient._id)}/split`,
        {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phone.trim(),
          dob: dob.trim(),
        },
      );
      onSplit?.(r.data.client);
    } catch (e) {
      toast.error("Split failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="client-split-dialog">
        <DialogHeader>
          <DialogTitle>Add another person on this email</DialogTitle>
          <DialogDescription>
            <span className="font-mono">{sourceClient?.email}</span> currently has this record on it.
            Confirm who it represents, then enter the second person.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded border border-zinc-200 bg-zinc-50 p-3 space-y-2">
            <div className="text-2xs uppercase tracking-wider text-zinc-600 font-semibold">
              Step 1 — current record
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">First name *</Label>
                <Input value={srcFirstName}
                       onChange={(e) => setSrcFirstName(e.target.value)}
                       data-testid="split-source-first-name" />
              </div>
              <div>
                <Label className="text-xs">Last name</Label>
                <Input value={srcLastName}
                       onChange={(e) => setSrcLastName(e.target.value)}
                       data-testid="split-source-last-name" />
              </div>
            </div>
          </div>

          <div className="rounded border border-indigo-200 bg-indigo-50/50 p-3 space-y-2">
            <div className="text-2xs uppercase tracking-wider text-indigo-900 font-semibold">
              Step 2 — second person
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">First name *</Label>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                       autoFocus data-testid="split-first-name" />
              </div>
              <div>
                <Label className="text-xs">Last name</Label>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)}
                       data-testid="split-last-name" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Phone (optional)</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                     placeholder={sourceClient?.phone || ""}
                     data-testid="split-phone" />
            </div>
            <div>
              <Label className="text-xs">Date of birth (optional)</Label>
              <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)}
                     data-testid="split-dob" />
            </div>
          </div>

          {willCollide && (
            <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded p-2"
                 data-testid="split-collide-warning">
              Both records would end up with the same name. Give them distinct names.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || willCollide} data-testid="split-submit">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Split className="h-3.5 w-3.5 mr-1" />}
            {srcChanged ? "Save + create second person" : "Create second person"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

